import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { OnboardingGate } from './OnboardingGate';
import { AdminShell } from '@/components/layout/AdminShell';
import { PublicShell } from '@/components/layout/PublicShell';

import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { OtpPage } from '@/features/auth/OtpPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { OAuthCallbackPage } from '@/features/auth/OAuthCallbackPage';
import { PendingAccessPage } from '@/features/auth/PendingAccessPage';
import { BillingSuccessPage as StripeBillingSuccessPage } from '@/features/billing/BillingSuccessPage';
import { BrandProfilePage } from '@/features/onboarding/BrandProfilePage';
import { BrandKitPage } from '@/features/onboarding/BrandKitPage';
import { PricingPage } from '@/features/billing/PricingPage';
import { BillingPage } from '@/features/billing/BillingPage';
import { CheckoutSuccessPage, CheckoutCancelPage } from '@/features/billing/CheckoutResult';
import { HomePage } from '@/features/home/HomePage';
import { BrandsHubPage } from '@/features/brands/BrandsHubPage';
import { ProjectsListPage } from '@/features/project/ProjectsListPage';
import { AssetsPage } from '@/features/assets/AssetsPage';
import { PhotosPage } from '@/features/photos/PhotosPage';
import { BrandStudioPage } from '@/features/brand-studio/BrandStudioPage';
import { AgencyProfilePage } from '@/features/agency/AgencyProfilePage';
import { BrandProfilePage as BrandHomePage } from '@/features/brand/BrandProfilePage';
import { BrandXotskiesPage } from '@/features/brand/BrandXotskiesPage';
import { BrandProjectsPage } from '@/features/brand/BrandProjectsPage';
import { ProjectPlannerPage } from '@/features/project/ProjectPlannerPage';
import { EventMeasurePage } from '@/features/insights/EventMeasurePage';
// build-v2 F5 — same-day route & proximity map for a project.
import { RoutePage } from '@/features/route/RoutePage';
// build-v2 F5 — staffing & roster (drag-assign, conflicts, rollover).
import { StaffingPage } from '@/features/staffing/StaffingPage';
// build-v2 G9 — My Schedule (cross-brand agenda).
import { SchedulePage } from '@/features/schedule/SchedulePage';
// build-v2 G13 — Operations settings (warehouses + asset protocols).
import { OperationsPage } from '@/features/settings/OperationsPage';
import { StationListPage } from '@/features/stations/StationListPage';
import { StationDetailPage } from '@/features/stations/StationDetailPage';
import { QrPrintPage } from '@/features/stations/QrPrintPage';
import { StockListPage } from '@/features/stock/StockListPage';
import { BrandInventoryListPage } from '@/features/brand-inventory/BrandInventoryListPage';
import { BrandSettingsPage } from '@/features/settings/BrandSettingsPage';
import { BrandIdentityPage } from '@/features/brand-identity/BrandIdentityPage';
import { BrandStudioNewPage } from '@/features/brand-studio/BrandStudioNewPage';
import { SurveyTemplatesPage } from '@/features/surveys/SurveyTemplatesPage';
import { SurveyPreviewPage } from '@/features/surveys/SurveyPreviewPage';
import { ContactsPage } from '@/features/contacts/ContactsPage';
import { ContactDetailPage } from '@/features/contacts/ContactDetailPage';
import { InsightsOverviewPage } from '@/features/insights/InsightsOverviewPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { AccessManagerPage } from '@/features/access-manager/AccessManagerPage';
import { ActiveLogPage } from '@/features/active-log/ActiveLogPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { ComposeNotificationPage } from '@/features/notifications/ComposeNotificationPage';
import { PublicSurveyPage } from '@/features/public-survey/PublicSurveyPage';
import { FinishPage } from '@/features/public-survey/FinishPage';
import { ShortLinkRedirect } from '@/features/public-survey/ShortLinkRedirect';
import { SharedQrLanding } from '@/features/public-survey/SharedQrLanding';
// Caso 02 — Event report (admin + public share link).
import { EventReportPage } from '@/features/events/EventReportPage';
import { PublicEventReportPage } from '@/features/public-report/PublicEventReportPage';
// PRD §12 — Reporter View (magic-link, 24h, no account).
import { ReporterView } from '@/features/reporter/ReporterView';
// build-v2 F7 — Collaborative passwordless intake (contributor view).
import { IntakePage } from '@/features/intake/IntakePage';
// Caso 03 — Prize redemption (operator-facing).
import { RedeemPrizePage } from '@/features/redemption/RedeemPrizePage';
// Caso 04 — Post-event follow-up settings.
import { FollowUpSettingsPage } from '@/features/followup/FollowUpSettingsPage';
// Caso 13 — Outbound CRM integrations (webhook ships; OAuth providers stubbed).
import { IntegrationsPage } from '@/features/integrations/IntegrationsPage';
// Caso 08 — first consumer of @onxite/ox-survey-builder.
import { SurveyBuilderPage } from '@/features/surveys/SurveyBuilderPage';
// Caso 12 parte 2 — Event-to-event trend comparison.
import { EventComparisonPage } from '@/features/trends/EventComparisonPage';
// Caso 11 — Booth-operator station view.
import { OperatorStationPage } from '@/features/operator/OperatorStationPage';
// Caso 11 v4 — Public entry that exchanges a station-token URL for a
// short-lived operator session and routes into the operator view.
import { OperatorLoginPage } from '@/features/operator/OperatorLoginPage';
// Caso 10 — Live event monitor.
import { LiveMonitorPage } from '@/features/events/LiveMonitorPage';
// In-app Help Center — bilingual, searchable, screen-by-screen.
import { HelpPage } from '@/features/help/HelpPage';

const router = createBrowserRouter([
  // Public — no auth
  {
    element: <PublicShell />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/auth/otp', element: <OtpPage /> },
      { path: '/auth/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/auth/reset-password', element: <ResetPasswordPage /> },
      { path: '/auth/oauth-callback', element: <OAuthCallbackPage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/s/:surveyId/:stationId', element: <ShortLinkRedirect /> },
      // Shared QR — one code, several stations. Lands on a station picker.
      { path: '/sg/:groupId', element: <SharedQrLanding /> },
      { path: '/public-surveys', element: <PublicSurveyPage /> },
      { path: '/public-finish', element: <FinishPage /> },
      // Caso 02 — public share link for event-result reports.
      { path: '/r/:token', element: <PublicEventReportPage /> },
      // build-v2 F7 — Collaborative intake: passwordless scoped invite. The
      // contributor (venue stakeholder / brand owner) completes one slice.
      { path: '/intake/:token', element: <IntakePage /> },
      // PRD §12 — Reporter View: 24h magic-link, no account, scoped to one
      // event. Renders its own full-screen layout (header + tab nav).
      { path: '/reporter/:token', element: <ReporterView /> },
      // Caso 11 v4 — operator login: trades the URL token for a session.
      { path: '/operator-login', element: <OperatorLoginPage /> },
    ],
  },

  // Authed (protected)
  {
    element: <ProtectedRoute />,
    children: [
      // Onboarding routes — gate enforces (no shell, dedicated layout)
      {
        element: <OnboardingGate />,
        children: [
          {
            path: '/onboarding/brand-profile',
            element: <BrandProfilePage />,
          },
          { path: '/onboarding/brandkit', element: <BrandKitPage /> },
          { path: '/pending-access', element: <PendingAccessPage /> },

          // Admin shell — gate redirects out if onboarding incomplete
          {
            element: <AdminShell />,
            children: [
              { index: true, element: <HomePage /> },
              { path: 'brands', element: <BrandsHubPage /> },
              { path: 'brands/:id', element: <BrandStudioPage /> },
              { path: 'projects', element: <ProjectsListPage /> },
              { path: 'assets', element: <AssetsPage /> },
              // build-v2 F9 — photo social feed (reactions/comments/tags/approval).
              { path: 'photos', element: <PhotosPage /> },
              // build-v2 G9 — My Schedule (cross-brand agenda).
              { path: 'schedule', element: <SchedulePage /> },
              // In-app Help Center — reached from the `?` button in the TopBar
              // and from contextual `<HelpHint section="…" />` deep-links.
              { path: 'help', element: <HelpPage /> },
              { path: 'agency/:id', element: <AgencyProfilePage /> },
              { path: 'brand/:id', element: <BrandHomePage /> },
              { path: 'brand/:id/projects', element: <BrandProjectsPage /> },
              { path: 'brand/:id/xotskies', element: <BrandXotskiesPage /> },
              { path: 'brand-identity', element: <BrandIdentityPage /> },
              { path: 'brand-studio', element: <Navigate to="/brand-identity" replace /> },
              { path: 'brand-studio/new', element: <BrandStudioNewPage /> },
              { path: 'brand-studio/:id', element: <BrandStudioNewPage /> },

              { path: 'projects/:id', element: <ProjectPlannerPage /> },
              { path: 'projects/:projectId/events/:eventId', element: <EventMeasurePage /> },
              // build-v2 F5 — same-day route & proximity for a project.
              { path: 'projects/:id/route', element: <RoutePage /> },
              // build-v2 F5 — staffing & roster (drag-assign / conflicts / rollover).
              { path: 'projects/:id/staffing', element: <StaffingPage /> },
              // Caso 02 — admin-facing report. Mounted at /events/:id/report
              // so the existing `Ver reporte` CTA in the event detail just
              // needs to point here.
              { path: 'events/:id/report', element: <EventReportPage /> },
              // Caso 03 — booth operator's redemption flow.
              { path: 'redeem', element: <RedeemPrizePage /> },
              // Caso 04 — Post-event follow-up settings.
              { path: 'settings/followup', element: <FollowUpSettingsPage /> },
              // Caso 13 — Outbound CRM integrations (webhook fully working,
              // OAuth providers are listed but disabled until apps registered).
              { path: 'settings/integrations', element: <IntegrationsPage /> },
              // Caso 11 — Booth-operator's mobile-first station view.
              { path: 'operator/:id', element: <OperatorStationPage /> },
              // Caso 10 — Live event monitor (TV-friendly, polling-based v1).
              { path: 'events/:id/live', element: <LiveMonitorPage /> },

              { path: 'stations', element: <StationListPage /> },
              { path: 'stations/:id', element: <StationDetailPage /> },
              { path: 'stations/:id/qr', element: <QrPrintPage /> },

              { path: 'stock', element: <StockListPage /> },
              { path: 'brand-inventory', element: <BrandInventoryListPage /> },

              { path: 'surveys/templates', element: <SurveyTemplatesPage /> },
              { path: 'surveys/:id/preview', element: <SurveyPreviewPage /> },
              // Caso 08 v1 — Embeddable survey builder. `/builder` is a
              // detached draft; `/builder/:id` loads + saves an existing
              // survey via the new /xy-xurvey/:id/tree endpoints.
              { path: 'surveys/builder', element: <SurveyBuilderPage /> },
              { path: 'surveys/builder/:id', element: <SurveyBuilderPage /> },
              // Caso 12 parte 2 — Event trend comparison. Routes nest under
              // project / brand for natural deep-linking; a flat /trends
              // route uses query strings for external embeds.
              { path: 'projects/:id/trends', element: <EventComparisonPage /> },
              { path: 'brand/:id/trends', element: <EventComparisonPage /> },
              { path: 'trends', element: <EventComparisonPage /> },

              { path: 'contacts', element: <ContactsPage /> },
              { path: 'contacts/:id', element: <ContactDetailPage /> },
              { path: 'insights', element: <InsightsOverviewPage /> },

              { path: 'settings', element: <SettingsPage /> },
              { path: 'settings/brand', element: <BrandSettingsPage /> },
              { path: 'settings/billing', element: <BillingPage /> },
              { path: 'settings/access-manager', element: <AccessManagerPage /> },
              { path: 'settings/operations', element: <OperationsPage /> },
              { path: 'active-log', element: <ActiveLogPage /> },
              { path: 'profile', element: <ProfilePage /> },

              { path: 'notifications/compose', element: <ComposeNotificationPage /> },

              { path: 'billing/success', element: <CheckoutSuccessPage /> },
              { path: 'billing/cancel', element: <CheckoutCancelPage /> },
              // Stripe-backed return URL (production).
              { path: 'billing/stripe-success', element: <StripeBillingSuccessPage /> },
            ],
          },
        ],
      },
    ],
  },

  // Catch-all
  { path: '*', element: <Navigate to="/" replace /> },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
